import { catalogApi, labApi } from './api';
import type {
  CourseItem,
  CourseItemStatus,
  CourseManifest,
} from '../types/course';

/**
 * Course-manifest data source for the outline page + Learning Shell rail
 * (docs/ide-ux-spec.md §16).
 *
 * Real data now: fetches the live `/catalog/courses/:slug` endpoint
 * (lab-manager) — course metadata + its labs — and assembles a CourseManifest.
 * Per-item status is merged from the student's lab progress. Today the only
 * real item entities are labs, so the manifest is lab-only; lectures / readings
 * / assessments will appear once those entities exist server-side.
 *
 * The hand-authored STUB below remains a last-resort fallback (e.g. the backend
 * is unreachable, or the old `c1-python-for-ai` demo slug is hit), so the page
 * degrades instead of erroring.
 */

const STUB_MANIFESTS: Record<string, CourseManifest> = {
  'c1-python-for-ai': {
    slug: 'c1-python-for-ai',
    certificate: 'Certificate 1: AI Foundations',
    title: 'Python for AI',
    description:
      'Async Python patterns for high-concurrency AI applications — the foundation for every later course.',
    items: [
      {
        id: 'l1-1',
        type: 'lecture',
        title: 'Why async matters for AI workloads',
        status: 'completed',
        durationMin: 14,
        module: 'Concurrency foundations',
        content: { kind: 'video', ref: 'c1-async-intro', label: 'Why async (lecture)' },
      },
      {
        id: 'r1-1',
        type: 'reading',
        title: 'Event loops & coroutines',
        status: 'completed',
        durationMin: 20,
        module: 'Concurrency foundations',
        content: { kind: 'book', ref: 'c1-book#2.3', label: 'Coursebook §2.3' },
      },
      {
        id: 'lab-c1-async-http',
        type: 'lab',
        title: 'Build an async HTTP client for AI APIs',
        status: 'in_progress',
        durationMin: 90,
        module: 'Concurrency foundations',
        href: '/lab/lab-c1-async-http',
      },
      {
        id: 'l1-2',
        type: 'lecture',
        title: 'Rate limiting & exponential backoff',
        status: 'available',
        durationMin: 11,
        module: 'Resilience',
        content: { kind: 'video', ref: 'c1-backoff', label: 'Backoff (lecture)' },
      },
      {
        id: 'lab-c1-retry-backoff',
        type: 'lab',
        title: 'Add retry with backoff to the client',
        status: 'locked',
        durationMin: 75,
        module: 'Resilience',
        href: '/lab/lab-c1-retry-backoff',
      },
      {
        id: 'a1',
        type: 'assessment',
        title: 'Checkpoint: async fundamentals',
        status: 'locked',
        durationMin: 60,
        module: 'Resilience',
      },
    ],
  },
};

/**
 * The route an item opens at. Labs live in the existing IDE (`/lab/:labId`);
 * everything else opens in the item shell. Shared by the course outline and
 * the item shell's Prev/Next so navigation targets stay consistent.
 */
export function itemHref(slug: string, item: CourseItem): string {
  if (item.type === 'lab' && item.href) return item.href;
  return `/course/${slug}/${item.id}`;
}

/** Map a lab_progress status onto a course-item status. */
function mapLabStatus(progressStatus: string | undefined): CourseItemStatus {
  switch (progressStatus) {
    case 'completed':
      return 'completed';
    case 'in_progress':
      return 'in_progress';
    default:
      // No locking model yet (needs prerequisites); untouched labs are open.
      return 'available';
  }
}

/** Numeric-aware slug sort, e.g. lab-1-2 before lab-1-10. A pragmatic fallback
 *  while `display_order` is unseeded server-side. */
function bySlug(a: { slug?: string }, b: { slug?: string }): number {
  return (a.slug || '').localeCompare(b.slug || '', undefined, { numeric: true });
}

/** Best-effort map of labId → progress status for the current student. */
async function fetchProgressByLab(): Promise<Record<string, string>> {
  try {
    const res = await labApi.getProgress();
    const list: any[] = res.data?.progress ?? res.data ?? [];
    const map: Record<string, string> = {};
    for (const p of list) {
      const id = p.lab_id ?? p.labId ?? p.lab?.id;
      if (id) map[id] = p.status;
    }
    return map;
  } catch {
    // Unauthenticated / no progress yet — everything defaults to available.
    return {};
  }
}

/** Set of course_item ids the current student has completed, from the A4
 *  progress endpoint. Best-effort: unauthenticated / no progress → empty. */
async function fetchCompletedItemIds(slug: string): Promise<Set<string>> {
  try {
    const { data } = await catalogApi.getCourseProgress(slug);
    const items: any[] = data?.items ?? [];
    return new Set(items.filter((i) => i.completed).map((i) => i.itemId));
  } catch {
    return new Set();
  }
}

/** Map one A2 manifest item (mixed type) onto a CourseItem. */
function mapManifestItem(item: any, completed: Set<string>): CourseItem {
  const base: CourseItem = {
    id: item.id,
    type: item.type,
    title: item.title,
    module: item.module ?? undefined,
    durationMin: item.durationMin ?? undefined,
    // No locking model yet; an item is complete (from progress) or available.
    status: completed.has(item.id) ? 'completed' : 'available',
  };
  if (item.type === 'lab') {
    // Route into the IDE by the lab's UUID. lab-manager keys the entire
    // run/submit/instructions pipeline off the UUID (`labId`), NOT the slug —
    // the backend's own `item.href`/`labSlug` are slug-based and make the
    // `/lab/:labId` loader 500 with "invalid input syntax for type uuid".
    // A null `labId` is a preview scaffold with no runnable lab behind it, so
    // we leave `href` undefined and the outline renders a "being prepared"
    // state instead of a broken deep-link.
    base.href = item.labId ? `/lab/${item.labId}` : undefined;
  } else if (item.type === 'lecture') {
    base.content = { kind: 'video', ref: item.videoExternalId, label: item.title };
  } else if (item.type === 'reading') {
    base.content = { kind: 'book', ref: item.ref?.chapter ?? '', label: item.title };
  }
  return base;
}

/** Resolve a course manifest by slug. Prefers the A2 mixed manifest
 *  (lectures/readings/labs/assessments); falls back to the labs-only catalog,
 *  then the stub, so the page never hard-errors and degrades before the
 *  curriculum importer has populated course_items on the server. */
export async function getCourseManifest(slug: string): Promise<CourseManifest | null> {
  // 1) A2 mixed manifest.
  try {
    const { data } = await catalogApi.getCourseManifest(slug);
    const course = data?.course;
    const rawItems: any[] = Array.isArray(data?.items) ? data.items : [];
    if (course && rawItems.length > 0) {
      const completed = await fetchCompletedItemIds(slug);
      return {
        slug: course.slug,
        // The reading endpoints key off the course id; fall back to the slug
        // when the catalog payload doesn't carry an explicit id.
        courseId: course.id ?? course.slug,
        certificate: course.certificate?.title ?? '',
        title: course.title,
        description: course.shortDescription ?? undefined,
        items: rawItems.map((it) => mapManifestItem(it, completed)),
      };
    }
    // empty manifest (importer not yet run) → fall through to labs-only
  } catch {
    // manifest endpoint unavailable → fall through
  }

  // 2) Labs-only catalog (pre-A2 behaviour).
  try {
    const { data } = await catalogApi.getCourse(slug);
    const course = data?.course;
    const labs: any[] = data?.labs ?? [];
    if (!course) return STUB_MANIFESTS[slug] ?? null;

    const progressByLab = await fetchProgressByLab();
    const items: CourseItem[] = [...labs].sort(bySlug).map((lab): CourseItem => ({
      id: lab.id,
      type: 'lab',
      title: lab.title,
      status: mapLabStatus(progressByLab[lab.id]),
      durationMin: lab.estimatedMinutes,
      href: `/lab/${lab.id}`,
    }));

    return {
      slug: course.slug,
      courseId: course.id ?? course.slug,
      certificate: course.certificate?.title ?? '',
      title: course.title,
      description: course.shortDescription ?? course.description ?? undefined,
      items,
    };
  } catch {
    return STUB_MANIFESTS[slug] ?? null;
  }
}

/**
 * Reverse-lookup: which course/item does a lab belong to? Lets the lab IDE join
 * the Learning Shell (outline rail + breadcrumb) even though its route is
 * `/lab/:labId`. Uses the live catalog labs (each carries its course); returns
 * null if not found.
 */
export async function getCourseForLab(
  labId: string,
): Promise<{ slug: string; itemId: string; courseTitle: string } | null> {
  try {
    const { data } = await catalogApi.getLabs({ limit: 500 });
    const labs: any[] = data?.labs ?? [];
    const lab = labs.find((l) => l.id === labId);
    if (lab?.course?.slug) {
      return { slug: lab.course.slug, itemId: lab.id, courseTitle: lab.course.title };
    }
    return null;
  } catch {
    return null;
  }
}
